import { type ReactNode } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Layout } from '@/components/Layout';
import Login from '@/routes/Login';
import FirstLogin from '@/routes/FirstLogin';
import Dashboard from '@/routes/Dashboard';
import Agents from '@/routes/Agents';
import AgentDetail from '@/routes/AgentDetail';
import Activity from '@/routes/Activity';
import System from '@/routes/System';
import Settings from '@/routes/Settings';
import NotFound from '@/routes/NotFound';
import { useAuth } from '@/hooks/useAuth';

/**
 * RequireAuth — guard component that gates everything inside the Layout.
 * We can't use a real `loader()` for this because TanStack Query is the
 * source of truth for the current user and loaders run before React
 * mounts, so we guard inside the component tree instead.
 *
 * Redirects:
 *   - no session → /login
 *   - session but `must_change_password` → /first-login
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (mustChangePassword && location.pathname !== '/first-login') {
    return <Navigate to="/first-login" replace />;
  }

  return <>{children}</>;
}

function ProtectedLayout() {
  return (
    <RequireAuth>
      <Layout />
    </RequireAuth>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/first-login', element: <FirstLogin /> },
  {
    element: <ProtectedLayout />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'agents', element: <Agents /> },
      { path: 'agents/new', element: <Agents /> },
      { path: 'agents/:name/*', element: <AgentDetail /> },
      { path: 'activity/*', element: <Activity /> },
      { path: 'system/*', element: <System /> },
      { path: 'settings/*', element: <Settings /> },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

// Exported so tests and Storybook can render a single route if desired.
export { Outlet };
